/** Performance diagnostic aggregation — pure helpers for unit tests + live panel. */

export const PERF_SAMPLE_SECONDS = 15;
/** Frame budget for 30 FPS target. */
export const PERF_FRAME_BUDGET_MS = 1000 / 30;

export interface PerfPassCriteria {
  minAvgFps: number;
  maxP95Ms: number;
  maxDroppedPct: number;
}

export const PERF_PASS_CRITERIA: PerfPassCriteria = {
  minAvgFps: 30,
  maxP95Ms: 40,
  maxDroppedPct: 10,
};

export interface PerfSampleInput {
  frameTimesMs: number[];
  sampleSeconds: number;
  devicePixelRatio: number;
  cssWidth: number;
  cssHeight: number;
  renderer: string;
  vendor: string;
  postEffects: string[];
  assetLoadMs: number;
  userAgent?: string;
  deviceMemory?: number;
  hardwareConcurrency?: number;
  packId?: string;
}

export interface PerfReport {
  avgFps: number;
  p95FrameMs: number;
  droppedFramePct: number;
  frameCount: number;
  sampleSeconds: number;
  resolution: string;
  dpr: number;
  renderer: string;
  vendor: string;
  postEffects: string[];
  assetLoadMs: number;
  pass: boolean;
  criteria: PerfPassCriteria;
  notes: string[];
  userAgent: string;
  deviceMemory: number | null;
  hardwareConcurrency: number | null;
  packId: string | null;
  /** Wall time of this report generation (ISO). */
  capturedAt: string;
}

export function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  const clamped = Math.max(0, Math.min(100, p));
  const idx = (clamped / 100) * (sortedAsc.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo]!;
  const t = idx - lo;
  return sortedAsc[lo]! * (1 - t) + sortedAsc[hi]! * t;
}

/**
 * Aggregate frame samples into a pass/fail report.
 * Thresholds are fixed (PERF_PASS_CRITERIA) — sampleSeconds may shorten for smoke
 * timing but never changes the pass formulas.
 */
export function aggregatePerfReport(
  input: PerfSampleInput,
  nowIso = new Date(0).toISOString(),
): PerfReport {
  const times = input.frameTimesMs.filter((t) => Number.isFinite(t) && t > 0 && t < 5000);
  const sorted = [...times].sort((a, b) => a - b);
  const sum = times.reduce((a, b) => a + b, 0);
  const avgFrameMs = times.length ? sum / times.length : 0;
  const avgFps = avgFrameMs > 0 ? 1000 / avgFrameMs : 0;
  const p95FrameMs = percentile(sorted, 95);
  const dropped = times.filter((t) => t > PERF_FRAME_BUDGET_MS).length;
  const droppedFramePct = times.length ? (dropped / times.length) * 100 : 100;

  const criteria = PERF_PASS_CRITERIA;
  const pass =
    times.length > 0 &&
    avgFps >= criteria.minAvgFps &&
    p95FrameMs <= criteria.maxP95Ms &&
    droppedFramePct <= criteria.maxDroppedPct;

  const notes: string[] = [
    "Pass criteria target mid-range phone: avg FPS ≥ 30, p95 ≤ 40 ms, dropped ≤ 10%.",
    "Headless SwiftShader / desktop GPU results do not prove mobile performance.",
    "Run on a real mid-range phone via the served app with ?scenario=<id>&perf=1.",
  ];
  if (times.length < 30) {
    notes.push("Insufficient frame samples: diagnostic may be incomplete.");
  }
  if (input.sampleSeconds < PERF_SAMPLE_SECONDS) {
    notes.push(
      `Sample window ${input.sampleSeconds}s (shorter than ${PERF_SAMPLE_SECONDS}s) — thresholds unchanged.`,
    );
  }

  return {
    avgFps: round1(avgFps),
    p95FrameMs: round1(p95FrameMs),
    droppedFramePct: round1(droppedFramePct),
    frameCount: times.length,
    sampleSeconds: input.sampleSeconds,
    resolution: `${Math.round(input.cssWidth * input.devicePixelRatio)}×${Math.round(input.cssHeight * input.devicePixelRatio)} (css ${Math.round(input.cssWidth)}×${Math.round(input.cssHeight)})`,
    dpr: input.devicePixelRatio,
    renderer: input.renderer || "(unknown)",
    vendor: input.vendor || "(unknown)",
    postEffects: [...input.postEffects],
    assetLoadMs: Math.round(input.assetLoadMs),
    pass,
    criteria,
    notes,
    userAgent: input.userAgent ?? "(unknown)",
    deviceMemory: input.deviceMemory ?? null,
    hardwareConcurrency: input.hardwareConcurrency ?? null,
    packId: input.packId ?? null,
    capturedAt: nowIso,
  };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export const DEFAULT_POST_EFFECTS = [
  "Bloom",
  "HueSaturation",
  "BrightnessContrast",
  "Vignette",
  "ACESFilmicToneMapping",
] as const;

/** Read optional smoke duration override — never changes thresholds. */
export function resolvePerfSampleSeconds(search: string): number {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const raw = params.get("perfDuration") ?? params.get("perfduration");
  if (!raw) return PERF_SAMPLE_SECONDS;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return PERF_SAMPLE_SECONDS;
  return Math.min(PERF_SAMPLE_SECONDS, Math.max(1, Math.floor(n)));
}

export function shouldShowPerfPanel(search: string): boolean {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return params.get("perf") === "1";
}
